"use client";

import { useMemo } from "react";

import { LayersFiltersList } from "@/components/flowbite/layers-filters-list";
import type { LayersFiltersListItem } from "@/components/flowbite/layers-filters-list";
import { EyeCloseIcon, EyeEmptyIcon } from "@/components/icons-lib";
import { dictionaries } from "@/dictionaries/content";
import { SchoolLevelType } from "@/providers/store/api/schools/schools.interface";
import { SCHOOL_LEVEL_TYPE_LABELS } from "@/shared/utils/school-level-display.utils";

import { useFindMySchoolLayers } from "./find-my-school-layers-context";

interface FindMySchoolSchoolLevelLayerOption {
  id: string;
  type: SchoolLevelType;
}

const SCHOOL_LEVEL_LAYER_OPTIONS: readonly FindMySchoolSchoolLevelLayerOption[] = [
  { id: "elementary", type: SchoolLevelType.ELEMENTARY },
  { id: "middle", type: SchoolLevelType.MIDDLE },
  { id: "high", type: SchoolLevelType.HIGH },
  { id: "k8", type: SchoolLevelType.K8 },
];

export interface FindMySchoolLayersContentProps {
  hasDistrictLayer?: boolean;
}

/** Layers-only content for Find My School map panel (no title, no National/New Housing). */
export function FindMySchoolLayersContent({ hasDistrictLayer = false }: FindMySchoolLayersContentProps) {
  const {
    districtVisible,
    setDistrictVisible,
    schoolBoundariesHiddenTypes,
    toggleSchoolBoundaryType,
    districtSchoolLevelTypes,
    publishedMapLevels,
    mapLevelsHiddenIds,
    toggleMapLevelId,
  } = useFindMySchoolLayers();

  const districtItem = useMemo((): LayersFiltersListItem | null => {
    if (!hasDistrictLayer) return null;

    return {
      id: "myDistrict",
      label: dictionaries.mapPage.layersVisibility.myDistrictLayer.label,
      icon: districtVisible ? (
        <EyeEmptyIcon className="h-7 w-7" />
      ) : (
        <EyeCloseIcon className="h-7 w-7" />
      ),
      active: !districtVisible,
      onIconClick: () => setDistrictVisible(!districtVisible),
    };
  }, [hasDistrictLayer, districtVisible, setDistrictVisible]);

  const schoolLevelItems = useMemo((): LayersFiltersListItem[] => {
    return SCHOOL_LEVEL_LAYER_OPTIONS.filter(({ type }) =>
      districtSchoolLevelTypes.includes(type),
    ).map(({ id, type }) => ({
      id: `school-${id}`,
      label: `${SCHOOL_LEVEL_TYPE_LABELS[type]} Schools`,
      icon: schoolBoundariesHiddenTypes.includes(type) ? (
        <EyeCloseIcon className="h-7 w-7" />
      ) : (
        <EyeEmptyIcon className="h-7 w-7" />
      ),
      active: schoolBoundariesHiddenTypes.includes(type),
      onIconClick: () => toggleSchoolBoundaryType(type),
    }));
  }, [districtSchoolLevelTypes, schoolBoundariesHiddenTypes, toggleSchoolBoundaryType]);

  const mapLevelItems = useMemo((): LayersFiltersListItem[] => {
    return publishedMapLevels.map((level) => {
      const hidden = mapLevelsHiddenIds.includes(level.id);
      return {
        id: `map-level-${level.id}`,
        label: level.name,
        icon: hidden ? (
          <EyeCloseIcon className="h-7 w-7" />
        ) : (
          <EyeEmptyIcon className="h-7 w-7" />
        ),
        active: hidden,
        onIconClick: () => toggleMapLevelId(level.id),
      };
    });
  }, [publishedMapLevels, mapLevelsHiddenIds, toggleMapLevelId]);

  const items = useMemo((): LayersFiltersListItem[] => {
    const list: LayersFiltersListItem[] = [];
    if (districtItem) list.push(districtItem);
    list.push(...schoolLevelItems);
    list.push(...mapLevelItems);
    return list;
  }, [districtItem, schoolLevelItems, mapLevelItems]);

  return (
    <div className="h-fit">
      <LayersFiltersList
        items={items}
        variant="alignWithTitle"
      />
    </div>
  );
}
